import React, { useState } from "react";
import DynamicTextField from "../../../../Common/Textfield/Textfield";
import DynamicSelectInput from "../../../../Common/Select-Input/SelectInput";
import PhoneNumberInput from "../../../../Common/PhoneNumberInput/PhoneNumberInput";
import AutoComplete from "../../../../Common/AutoComplete/AutoComplete";
import OptionsData from "../../../../Assets/Json/StaticOptions.json";
import DynamicAutocomplete from "../../../../Common/AutoSuggestion/DynamicAutocomplete.js";
import CandidateAcademicsEdit, {
  AcademicsEducationDetailsEdit,
  CareerInfoEdit,
} from "./CandidateAcademicsEdit";
import DynamicCandidateCustomField from "../../../../Common/DynamicCandidateCustomField/DynamicCandidateCustomField.jsx";
import TextArea from "../../../../Common/TextArea/TextArea.js";

const sectionStyle = {
  background: "var(--sidepopup-background)",
  border: "1px solid var(--accordian-border)",
  borderRadius: "12px",
  padding: "20px 24px",
  marginBottom: "16px",
  overflow: "hidden",
};

const sectionHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "16px",
  cursor: "pointer",
  userSelect: "none",
};

const sectionTitleStyle = {
  fontWeight: 600,
  fontSize: "15px",
  color: "var(--header__color)",
};

const sectionBadgeStyle = {
  fontSize: "11px",
  fontWeight: 600,
  background: "#6860ee22",
  color: "#6860ee",
  borderRadius: "20px",
  padding: "2px 8px",
  marginLeft: "8px",
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "16px",
};

const fieldLabelStyle = {
  fontSize: "14px",
  fontWeight: 500,
  color: "var(--text-color-accent)",
};

const inputStyle = {
  width: "100%",
  color: "var(--header__color)",
  background: "none",
  border: "1px solid var(--accordian-border)",
  borderRadius: "12px",
};

const hasExperienceValue = (value) =>
  value !== undefined && value !== null && value !== "";

const getExperienceInputValue = (candidate) => {
  if (hasExperienceValue(candidate?.years_of_experience)) {
    return candidate.years_of_experience;
  }

  const parsedExperience =
    candidate?.parsedResumeData?.BasicInfo?.YearsOfExperience;

  return hasExperienceValue(parsedExperience) ? parsedExperience : "";
};

function EditSection({ title, badge, children, defaultOpen = true }) {
  const [open, setOpen] = React.useState(defaultOpen);

  return (
    <div style={sectionStyle}>
      <div style={sectionHeaderStyle} onClick={() => setOpen((p) => !p)}>
        <span style={{ display: "flex", alignItems: "center" }}>
          <span style={sectionTitleStyle}>{title}</span>
          {badge !== undefined && (
            <span style={sectionBadgeStyle}>{badge}</span>
          )}
        </span>
        <span
          style={{
            color: "#6860ee",
            fontSize: "12px",
            transition: "transform 0.2s",
            display: "inline-block",
            transform: open ? "rotate(0deg)" : "rotate(-180deg)",
          }}
        >
          ▲
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateRows: open ? "1fr" : "0fr",
          transition:
            "grid-template-rows 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease",
          opacity: open ? 1 : 0,
          overflow: "hidden",
        }}
      >
        <div style={{ minHeight: "0px" }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, fullWidth = false, labelStyle }) {
  return (
    <div style={fullWidth ? { gridColumn: "1 / -1" } : {}}>
      <div style={{ ...fieldLabelStyle, ...labelStyle }}>{label}</div>
      {children}
    </div>
  );
}

function CandidateEditForm({
  candidate,
  editedCandidateData,
  handleCandidateChange,
  candidateCustomFields = [],
  vendorList,
  vendorOptionLoading,
  handleVendorInputChange,
  assignedVendor,
  isVendorFeatureEnabled,
  industryOptions = [],
}) {
  const { StaticOptions = {} } = OptionsData || {};
  const {
    skills: skillOptions = [],
  } = StaticOptions;
  const [vendorInput, setVendorInput] = useState(() => {
    const vendorId =
      editedCandidateData?.vendorID !== undefined
        ? editedCandidateData.vendorID
        : assignedVendor?.id || "";
    if (vendorId && assignedVendor?.id === vendorId) {
      return assignedVendor.contact_person
        ? `${assignedVendor.name} - ${assignedVendor.contact_person}`
        : assignedVendor.name;
    }
    return "";
  });

  const skillsToDisplay = Array.isArray(candidate?.skills)
    ? candidate.skills
    : candidate?.parsedResumeData?.Skills || [];

  const currentSkills =
    editedCandidateData?.skills !== undefined
      ? Array.isArray(editedCandidateData.skills)
        ? editedCandidateData.skills
        : []
      : skillsToDisplay;

  const filteredSkillOptions = skillOptions.filter(
    (skill) =>
      !currentSkills.some((s) => s.toLowerCase() === skill.title.toLowerCase()),
  );

  const handleSkillChange = (selectedOptions) => {
    const validSkills = selectedOptions
      .map((option) => {
        if (typeof option === "string") return option.trim();
        if (option && typeof option === "object" && option.title)
          return option.title.trim();
        return "";
      })
      .filter(Boolean);

    const uniqueSkills = validSkills.filter(
      (skill, index, self) =>
        index ===
        self.findIndex((s) => s.toLowerCase() === skill.toLowerCase()),
    );

    handleCandidateChange({ skills: uniqueSkills });
  };

  const val = (key, fallbackKey) => {
    if (editedCandidateData?.[key] !== undefined)
      return editedCandidateData[key];
    if (candidate?.[key] !== undefined) return candidate[key];
    if (
      fallbackKey &&
      candidate?.parsedResumeData?.BasicInfo?.[fallbackKey] !== undefined
    )
      return candidate.parsedResumeData.BasicInfo[fallbackKey];
    return "";
  };

  const change = (key) => (e) =>
    handleCandidateChange({ [key]: e.target.value });

  const getEducationHistoryLength = () => {
    const educationDetails =
      editedCandidateData.EducationHistory !== undefined
        ? Array.isArray(editedCandidateData.EducationHistory)
          ? editedCandidateData.EducationHistory
          : []
        : candidate?.parsedResumeData?.EducationHistory || [];
    return educationDetails.length;
  };
  const getCertificationsLength = () => {
    const certifications =
      editedCandidateData?.CertificationsAndLicenses !== undefined
        ? Array.isArray(editedCandidateData.CertificationsAndLicenses)
          ? editedCandidateData.CertificationsAndLicenses
          : []
        : candidate?.parsedResumeData?.CertificationsAndLicenses || [];
    return certifications.length;
  };
  const getCareerInfoLength = () => {
    const careerInfo =
      editedCandidateData?.CareerHistory?.JobDetails !== undefined
        ? Array.isArray(editedCandidateData.CareerHistory?.JobDetails)
          ? editedCandidateData.CareerHistory?.JobDetails
          : []
        : candidate?.parsedResumeData?.CareerHistory?.JobDetails || [];
    return careerInfo.length;
  };

  const educationCount = getEducationHistoryLength();
  const certificationsCount = getCertificationsLength();
  const careerInfoCount = getCareerInfoLength();

  const getCustomFieldValue = (field) => {
    const editedValue = editedCandidateData?.custom_fields?.[field.field_key];
    if (editedValue !== undefined) return editedValue;
    return field.value;
  };

  const handleCustomFieldChange = (fieldKey, value) => {
    const baseCustomFields = candidateCustomFields.reduce(
      (acc, field) => ({
        ...acc,
        [field.field_key]: getCustomFieldValue(field),
      }),
      {},
    );

    handleCandidateChange({
      custom_fields: {
        ...baseCustomFields,
        [fieldKey]: value,
      },
    });
  };

  return (
    <div style={{ paddingBottom: "24px" }}>
      {/* Skills */}
      <EditSection title="Skills" badge={currentSkills.length}>
        <div style={fieldLabelStyle}>Add or remove skills</div>
        <AutoComplete
          multiple
          placeholder="Search and add skills"
          value={currentSkills}
          options={filteredSkillOptions}
          tags={true}
          onChange={handleSkillChange}
          InputWrapperStyle={{
            minHeight: "60px",
            maxHeight: "160px",
            overflowY: "auto",
            padding: "5px",
            border: "1px solid var(--accordian-border)",
            borderRadius: "8px",
          }}
          showClearAll={true}
        />
      </EditSection>

      {/* Contact Info */}
      <EditSection title="Contact Info">
        <div style={gridStyle}>
          <Field label="Full Name">
            <DynamicTextField
              name="name"
              placeholder="Enter full name"
              value={val("name", "Name")}
              onChange={change("name")}
              style={inputStyle}
            />
          </Field>

          <Field label="Email">
            <DynamicTextField
              name="email"
              placeholder="Enter email"
              value={val("email", "Email")}
              onChange={change("email")}
              style={inputStyle}
            />
          </Field>

          <Field label="Phone" labelStyle={{ marginBottom: "16px" }}>
            <PhoneNumberInput
              name="phone"
              value={val("phone", "PhoneNumber")}
              onChange={(phone) => {
                const normalized = phone?.startsWith("+") ? phone : `+${phone}`;
                handleCandidateChange({ phone: normalized });
              }}
              width="100%"
              height="40px"
              placeholder="Enter phone number"
              InputWrapperStyle={{
                width: "100%",
                background: "none",
                border: "1px solid var(--accordian-border)",
                borderRadius: "12px",
              }}
            />
          </Field>

          <Field label="Secondary Email">
            <DynamicTextField
              name="secondaryEmail"
              placeholder="Enter secondary email"
              value={val("secondaryEmail", "SecondaryEmail")}
              onChange={change("secondaryEmail")}
              style={inputStyle}
            />
          </Field>

          <Field label="Secondary Phone">
            <DynamicTextField
              name="secondaryPhone"
              placeholder="Enter secondary phone"
              value={val("secondaryPhone", "SecondaryPhone")}
              onChange={change("secondaryPhone")}
              style={inputStyle}
            />
          </Field>

          <Field label="Role / Designation">
            <DynamicTextField
              name="designation"
              placeholder="Enter designation"
              value={
                editedCandidateData?.designation !== undefined
                  ? editedCandidateData.designation
                  : candidate?.designation?.trim()
                    ? candidate.designation
                    : candidate?.parsedResumeData?.BasicInfo?.Designation
                      ? candidate.parsedResumeData.BasicInfo.Designation
                      : candidate?.parsedResumeData?.CareerHistory
                          ?.JobDetails?.[0]?.JobTitle || ""
              }
              onChange={change("designation")}
              style={inputStyle}
            />
          </Field>

          <Field 
            label="Source"
          >
            <DynamicTextField
              name="source"
              placeholder="Select source"
              type="text"
              value={
                editedCandidateData?.source !== undefined
                  ? editedCandidateData.source
                  : candidate?.source?.trim()
                    ? candidate.source
                    : ""
              }
              onChange={change("source")}
              style={inputStyle}
            />
          </Field>

          <Field 
          label="Industry"
          labelStyle={{marginBottom: "15px"}}
          >
            <DynamicSelectInput
              name="industry"
              defaultOption="Select industry"
              options={industryOptions}
              value={
                editedCandidateData?.industry !== undefined
                  ? editedCandidateData.industry
                  : candidate?.industry?.trim()
                    ? candidate.industry
                    : ""
              }
              onChange={change("industry")}
              selectStyle={{
                ...inputStyle,
                height: "40px",
                fontFamily: "Satoshi",
                fontSize: "15px",
                fontWeight: 500,                
              }}
              wrapperStyle={{ width: "100%" }}
              defaultOptionStyle={{ color: "#9AA0A6" }}
            />
          </Field>
          <Field label="Remarks">
            <TextArea
              name="remarks"
              value={
                editedCandidateData?.remarks !== undefined
                  ? editedCandidateData.remarks
                  : candidate?.remarks?.trim()
                    ? candidate.remarks
                    : ""
              }
              onChange={change("remarks")}
            />
          </Field>
        </div>
      </EditSection>

      {/* Address */}
      <EditSection title="Address">
        <div style={gridStyle}>
          <Field label="Location / City">
            <DynamicTextField
              name="city"
              placeholder="Enter city"
              value={val("city", "CurrentLocationCity")}
              onChange={change("city")}
              style={inputStyle}
            />
          </Field>

          <Field label="State">
            <DynamicTextField
              name="state"
              placeholder="Enter state"
              value={val("state", "CurrentLocationState")}
              onChange={change("state")}
              style={inputStyle}
            />
          </Field>

          <Field label="Country">
            <DynamicTextField
              name="country"
              placeholder="Enter country"
              value={val("country", "CurrentLocationCountry")}
              onChange={change("country")}
              style={inputStyle}
            />
          </Field>

          <Field label="Address Line 1">
            <DynamicTextField
              name="lineAddress1"
              placeholder="Enter address"
              value={val("lineAddress1", "line_address_1")}
              onChange={change("lineAddress1")}
              style={inputStyle}
            />
          </Field>

          <Field label="Address Line 2">
            <DynamicTextField
              name="lineAddress2"
              placeholder="Enter address"
              value={val("lineAddress2", "line_address_2")}
              onChange={change("lineAddress2")}
              style={inputStyle}
            />
          </Field>

          <Field label="Zip Code">
            <DynamicTextField
              name="zipCode"
              placeholder="Enter zip code"
              value={val("zipCode", "zip_code")}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || /^\d*$/.test(v))
                  handleCandidateChange({ zipCode: v });
              }}
              style={inputStyle}
            />
          </Field>
        </div>
      </EditSection>

      {/* Compensation & Work Status */}
      <EditSection title="Compensation & Work Status">
        <div style={gridStyle}>
          <Field label="Experience (years)">
            <DynamicTextField
              name="experience"
              placeholder="e.g. 3.5"
              value={
                editedCandidateData?.experience !== undefined
                  ? editedCandidateData.experience
                  : getExperienceInputValue(candidate)
              }
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || /^\d*\.?\d*$/.test(value)) {
                  handleCandidateChange({
                    experience: value,
                  });
                }
              }}
              style={inputStyle}
            />
          </Field>

          <Field label="Preferred Salary">
            <DynamicTextField
              name="preferredSalary"
              placeholder="Enter preferred salary"
              type="number"
              value={val("preferredSalary", "preferred_salary")}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || /^\d*\.?\d*$/.test(v))
                  handleCandidateChange({ preferredSalary: v });
              }}
              style={inputStyle}
            />
          </Field>

          <Field label="Visa Status">
            <DynamicTextField
              name="visaStatus"
              placeholder="Enter visa status"
              value={val("visaStatus", "VisaStatus")}
              onChange={change("visaStatus")}
              style={inputStyle}
            />
          </Field>
        </div>
      </EditSection>

      {candidateCustomFields.length > 0 && (
        <EditSection title="Other Fields">
          <div style={gridStyle}>
            {candidateCustomFields.map((field) => (
              <Field key={field.field_key || field.id}>
                <DynamicCandidateCustomField
                  entity_type={field.entity_type}
                  field_key={field.field_key}
                  label={field.label}
                  field_type={field.field_type}
                  required={field.required}
                  options={field.options}
                  value={getCustomFieldValue(field)}
                  onChange={(value) =>
                    handleCustomFieldChange(field.field_key, value)
                  }
                  canEditToggleStyle={{marginTop: "41px"}}
                />
              </Field>
            ))}
          </div>
        </EditSection>
      )}

      {isVendorFeatureEnabled && (
        <EditSection title="Vendor">
          <Field label="Vendor (Optional)">
            <DynamicAutocomplete
              placeholder="Select Vendor"
              minCharsForSearch={3}
              noOptionsText="No Vendor found."
              options={vendorList || []}
              loading={vendorOptionLoading}
              value={(() => {
                const vendorId =
                  editedCandidateData?.vendorID !== undefined
                    ? editedCandidateData.vendorID
                    : assignedVendor?.id || "";

                let updatedVendorList = vendorList || [];

                if (
                  vendorId &&
                  !updatedVendorList.some((v) => v.value === vendorId) &&
                  assignedVendor?.name
                ) {
                  updatedVendorList = [
                    {
                      label: assignedVendor.contact_person
                        ? `${assignedVendor.name} - ${assignedVendor.contact_person}`
                        : assignedVendor.name,
                      value: assignedVendor.id,
                    },
                    ...updatedVendorList,
                  ];
                }

                return (
                  updatedVendorList.find((v) => v.value === vendorId) || null
                );
              })()}
              inputValue={vendorInput}
              onInputChange={(event, newInputValue) => {
                setVendorInput(newInputValue);
                handleVendorInputChange(event, newInputValue);
              }}
              onChange={(value) => {
                handleCandidateChange({
                  vendorID: value?.value || "",
                });

                setVendorInput(value?.label || "");
              }}
              sx={{
                mt: "5px",
                ".MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  height: "40px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid var(--border__color) !important",
                    borderRadius: "12px",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6860ee",
                    borderRadius: "12px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#7a70f2",
                    borderRadius: "12px",
                  },
                },
                ".MuiInputBase-input": {
                  color: "var(--header__color)",
                  padding: "8px 12px !important",
                  height: "40px",
                  fontFamily: "Satoshi !important",
                },
              }}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: "var(--body__background)",
                    color: "var(--header__color)",
                    borderRadius: "12px",
                    boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
                    border: "1px solid var(--border__color)",
                  },
                },
                listbox: {
                  sx: {
                    "& li": {
                      fontSize: "14px",
                      fontWeight: 500,
                      padding: "8px 16px",
                      color: "var(--header__color)",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.04)",
                      },
                    },
                  },
                },
              }}
            />
          </Field>
        </EditSection>
      )}

      <EditSection title="Education Details" badge={educationCount}>
        <Field>
          <AcademicsEducationDetailsEdit
            candidate={candidate}
            editedCandidateData={editedCandidateData}
            handleCandidateChange={handleCandidateChange}
          />
        </Field>
      </EditSection>

      <EditSection
        title="Certifications & Licenses"
        badge={certificationsCount}
      >
        <Field>
          <CandidateAcademicsEdit
            candidate={candidate}
            editedCandidateData={editedCandidateData}
            handleCandidateChange={handleCandidateChange}
          />
        </Field>
      </EditSection>

      <EditSection title="Career Information" badge={careerInfoCount}>
        <Field>
          <CareerInfoEdit
            candidate={candidate}
            editedCandidateData={editedCandidateData}
            handleCandidateChange={handleCandidateChange}
          />
        </Field>
      </EditSection>
    </div>
  );
}

export default CandidateEditForm;
